import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Reservation, Reservee} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {Checkout} from '@wix/ambassador-checkout/types'

import {reservationsService} from '../../../services/reservationsService'
import {ApiError, isWixHttpError} from '../../../utils/errors'
import {RequestStatus} from '../../../utils/wrapRequest'
import {goToReservationConfirmation, navigateToCheckoutPage} from '../../../utils/navigation'
import {getLogger} from '../../../utils/getLogger'
import {checkoutService} from '../../../services/checkoutService'
import {isFreeCheckout} from '../../../utils/ecom'
import {trackTableReservedEvent} from '../../../utils/trackEvent'
import {EXPERIMENTS} from '../../../utils/constants'

import {isReservationSuccessful} from './utils'

// error codes for which we have separate modal titles
export enum ReserveReservationApplicationErrorCode {
  HELD_EXPIRED = 'HELD_EXPIRED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

// remove after specs.tableReservations.errorHandlerIntegrationLiveSite is merged
export interface ReserveReservationApiError
  extends ApiError<ReserveReservationApplicationErrorCode> {}

export const reserveReservation = (flowAPI: ControllerFlowAPI) => {
  return async (
    reservation: Reservation,
    reservee: Reservee,
    reservationLocation: ReservationLocation,
  ) => {
    let checkoutResponse: {checkout?: Checkout}
    const setProps = flowAPI.controllerConfig.setProps
    const {getResolvedError} = flowAPI.errorHandler
    const logger = getLogger(flowAPI.bi!)
    const isBusinessLocationIdExperimentEnabled = flowAPI.experiments.enabled(
      EXPERIMENTS.businessLocationId,
    )
    const isErrorHandlerIntegrationEnabled = flowAPI.experiments.enabled(
      EXPERIMENTS.errorHandlerIntegration,
    )

    try {
      setProps({submitReservationStatus: RequestStatus.LOADING})
      const finishedReservation = await reservationsService.reserveReservation(
        flowAPI,
        reservation.id ?? '',
        reservee,
        reservation.revision ?? '',
      )

      // this is the only place where can get just created reservation
      if (finishedReservation?.id) {
        logger.reservationCreated({
          isPreview: false,
          reservation: finishedReservation!,
          reservee,
          reservationLocation,
          isCardTokenization: false,
        })

        try {
          checkoutResponse = await checkoutService.createCheckout(
            flowAPI,
            {
              reservationId: finishedReservation.id,
              reservee,
              locationId: reservationLocation.location?.id ?? undefined,
            },
            isBusinessLocationIdExperimentEnabled,
          )
        } catch (error) {
          getResolvedError(error)
          if (isReservationSuccessful(finishedReservation)) {
            console.error('Checkout is undefined')

            await goToReservationConfirmation(flowAPI, finishedReservation.id)

            return
          } else {
            throw error
          }
        }

        if (!checkoutResponse?.checkout) {
          throw new Error('Checkout is undefined')
        }

        const isFreeReservation = isFreeCheckout(checkoutResponse.checkout)

        if (isFreeReservation) {
          if (finishedReservation?.id) {
            trackTableReservedEvent(flowAPI, finishedReservation.id, reservee)
          }

          try {
            await checkoutService.createOrderFromCheckout(flowAPI, checkoutResponse.checkout!.id!)
          } catch (error) {
            if (isReservationSuccessful(finishedReservation)) {
              console.error('Error creating order from checkout', error)
            } else {
              throw error
            }
          }
          await goToReservationConfirmation(flowAPI, finishedReservation.id)
        } else {
          await navigateToCheckoutPage(
            flowAPI,
            checkoutResponse.checkout!.id!,
            finishedReservation.id,
          )
        }
      } else {
        throw new Error('No reservation')
      }
    } catch (err: any) {
      const resolvedError = getResolvedError(err)
      const apiError: ReserveReservationApiError = {}

      // remove after specs.tableReservations.errorHandlerIntegrationLiveSite is merged
      const applicationErrorCode = err?.response?.data?.details?.applicationError?.code

      // remove after specs.tableReservations.errorHandlerIntegrationLiveSite is merged
      if (isWixHttpError(err) && applicationErrorCode) {
        apiError.applicationCode = applicationErrorCode
        apiError.message = err?.response?.data?.message
      } else {
        apiError.message = err.message
        apiError.applicationCode =
          err?.request?.status === 429
            ? ReserveReservationApplicationErrorCode.TOO_MANY_REQUESTS
            : undefined
      }

      setProps({
        submitReservationStatus: RequestStatus.FAILED,
        apiRequestErrorDetails: isErrorHandlerIntegrationEnabled ? resolvedError : apiError,
      })
    }
  }
}
